import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {Page, Card, Form, FormLayout, Spinner, SkeletonBodyText, Link, FooterHelp, Banner} from '@shopify/polaris'
import { useHistory } from 'react-router-dom'
import isEqual from 'react-fast-compare'
import axios from 'axios'

import TextInputComponent from '../../components/text-input'
import ColorPickerComponent from '../../components/color-picker-input'
import CheckboxComponent from '../../components/checkbox-input'
import PreviewImageComponent from '../../components/preview-image-input/preview-image-input'
import MapperPinsOptionsComponent from '../../components/mapper-pins-options'
import ContextualSaveBarComponent from '../../components/custom-contextual-save-bar'
import PromptAlertComponent from '../../components/prompt-alert'

import {
  actionSetActivePin,
  actionSetSelectedItem,
  actionSetToastMessage,
  actionToggleToastActive,
  actionSetToastError,
  actionUpdateItemField
} from '../../store/actions'
import { renameKey } from '../../libs/utils'
import useShouldSendRequest from '../../hooks/useShouldSendRequest'

const EditMapperComponent = (props) => {
  const editingId = props.match ? props.match.params.id : false
  const history = useHistory()
  const dispatch = useDispatch()
  const activeItem = useSelector(state => state.activeItem)
  const originalItem = useRef({...activeItem})
  const activePinIndex = useSelector(state => state.activePin)
  const [imageDimension, setImageDimension] = useState({})
  const [loading, setLoading] = useState(false)
  const [presetList, setPresetList] = useState([])
  const [presetData, setPresetData] = useState([])
  const [missingTitle, setMissingTitle] = useState(false)
  const [missingImage, setMissingImage] = useState(false)
  const shouldLoading = useShouldSendRequest(
    `/api/v2/admin/mapper/${editingId}`,
    editingId && !Object.keys(activeItem).length,
    (data) => {
      originalItem.current = data
    }
  )

  // load presets
  useEffect(() => {
    async function getAllPresets() {
      const list = [
        {
          label: 'Custom',
          value: 0
        }
      ]
      const res = await axios.get('/api/v2/admin/presets')
      if (res.data.entries && res.data.entries.length) {
        const pinPresets = res.data.entries.map(entry => {
          const name = entry.preset_name && entry.preset_name.length
            ? entry.preset_name
            : 'Untitled preset'
          return {
            label: name,
            value: parseInt(entry.id)
          }
        })
        setPresetList([
          ...pinPresets,
          ...list
        ])
        setPresetData(res.data.entries)
      } else {
        setPresetList(list)
      }
    }

    getAllPresets()
  }, [])

  const saveAction = (shouldRedirectToGetCode = false) => {
    setLoading(true)
    const mapperData = renameKey(activeItem, 'pins', 'pins_attributes')
    let endpoint, data
    if (editingId) {
      endpoint = '/api/v2/admin/mappers/save'
      data = {
        id: editingId,
        mapper: mapperData
      }
    } else {
      endpoint = '/api/v2/admin/mappers/new'
      data = {
        mapper: mapperData
      }
    }
    if (
      Object.keys(activeItem).length > 0 &&
      activeItem.name
      && activeItem.name.length
      && activeItem.image_url
      && activeItem.image_url.length
    ) {
      axios.post(endpoint, data)
        .then((res) => {
          originalItem.current = activeItem
          setLoading(false)
          if (shouldRedirectToGetCode) {
            history.push(`/mappers/${res.data.id}/code`)
          }
        })
        .catch((e) => {
          console.log(e)
        })
    } else {
      dispatch(actionSetToastMessage('Some mapper fields are required.'))
      dispatch(actionToggleToastActive(true))
      if (!activeItem.name.length) setMissingTitle(true)
      if (!activeItem.image_url.length) setMissingImage(true)
      setLoading(false)
    }
  }

  return (
    <div>
      <Page fullWidth>
        <Banner status="info">
          <p>Please note that because modern browsers cache the javascript files, it may take some time for your storefront to reflect saved changes, however, you can always see the new changes in the private/incognito window of your browser as private window is never cached by the browser.</p>
        </Banner>
      </Page>
      <Page
        fullWidth
        breadcrumbs={[{
          content: 'Mappers',
          url: '/'
        }]}
        title={editingId ? 'Edit mapper' : 'Add mapper'}>
        {shouldLoading && <SkeletonBodyText />}
        {!shouldLoading && Object.keys(activeItem).length > 0 && <div className={loading ? 'loading-wrapper' : null}>
          {loading && <Spinner accessibilityLabel="Saving data" size="large" color="teal" />}
          {!isEqual(originalItem.current, activeItem) && <ContextualSaveBarComponent
            onDiscardAction={() => {
              dispatch(actionSetSelectedItem(originalItem.current))
            }}
            onSaveAction={() => {
              saveAction(!editingId)
              dispatch(actionSetToastMessage(editingId ? 'Mapper saved' : 'Mapper created'))
              dispatch(actionSetToastError(false))
              dispatch(actionToggleToastActive(true))
            }}
          />}
          <PromptAlertComponent when={!isEqual(originalItem.current, activeItem)}/>
          <Card sectioned>
            <Form noValidate onSubmit={() => {}}>
              <FormLayout>
                <TextInputComponent
                  name='name'
                  value={activeItem.name}
                  onChange={(key, value) => {
                    dispatch(actionUpdateItemField(key, value))
                    if (value && value.length) setMissingTitle(false)
                  }}
                  label='Mapper name'
                  error={missingTitle}
                />
                <CheckboxComponent
                  label='Show modal on mouse hover instead of click'
                  name='disable_tooltip'
                  checked={activeItem.disable_tooltip}
                  onChange={(key, value) => dispatch(actionUpdateItemField(key, value))}
                />
                <CheckboxComponent
                  label='Force mapper fullwidth'
                  name='full_width'
                  checked={activeItem.full_width}
                  onChange={(key, value) => dispatch(actionUpdateItemField(key, value))}
                />
                <CheckboxComponent
                  label='Enable image carousel on quickview modal'
                  name='enable_quickview_carousel'
                  checked={activeItem.enable_quickview_carousel}
                  onChange={(key, value) => dispatch(actionUpdateItemField(key, value))}
                />
                <CheckboxComponent
                  label='Enable background overlay on modal opened'
                  name='image_overlay'
                  checked={activeItem.image_overlay}
                  onChange={(key, value) => dispatch(actionUpdateItemField(key, value))}
                />
                {
                  activeItem.image_overlay &&
                  <ColorPickerComponent
                    label='Image overlay background'
                    name='image_overlay_color'
                    value={activeItem.image_overlay_color}
                    onChange={(key, value) => dispatch(actionUpdateItemField(key, value))}
                  />
                }
                <PreviewImageComponent
                  onImageLoaded={setImageDimension}
                  error={missingImage}
                  onChange={(key, value) => {
                    if (value && value.length) setMissingImage(false)
                  }}
                />
              </FormLayout>
            </Form>
          </Card>
          {
            activePinIndex !== null &&
            presetList.length &&
            <MapperPinsOptionsComponent
              presetList={presetList}
              presetData={presetData}
              imageDimension={imageDimension}
              showDeleteAction
              showDuplicateAction
              showSaveAction
              onSaveIsPromise={false}
              onSaveAction={() => dispatch(actionSetActivePin(null))}
            />
          }
        </div>}
        <FooterHelp>
          Learn more about<Link url="/docs#/mapper-settings" external> mapper settings</Link>
        </FooterHelp>
      </Page>
    </div>
  )
}

export default EditMapperComponent
